import { cn } from "../../../lib/utils";

/**
 * THE FACILITATOR OS EMPTY STATE. On the grid, in the content column, framed.
 *
 * ===================== THE DEFECT THIS EXISTS FOR IS NAMED IN THE BRIEF ====
 * Prepare's "there is no prep material here" renders as an `m-8 max-w-prose`
 * notice pinned to the top-left of a full-width panel while the resolved surface
 * renders at `mx-auto max-w-[1180px]`. The loader's own docblock records that
 * "no prep" is the ORDINARY state in production today, so a small off-centre
 * floating card is what most teachers see most of the time. Eight distinct empty
 * constructions ship across the OS; three of them (Prep empty, Prep failed,
 * Manage room-unavailable) read as broken rather than designed.
 *
 * ===================== THIS CHUNK BUILDS IT, 9.5 MOUNTS IT ON PREP =========
 * Prepare's fix is not a component swap. The notice sits OUTSIDE `PrepSurface`,
 * so it loses the sticky header and the assistant orb as well as the column;
 * repairing it means restructuring where the loader mounts, which is FE-9.5's
 * work and needs the surface's own state matrix in hand. What lands here is the
 * primitive, shaped so 9.5 drops it in without redesigning it, and one adoption
 * that IS a pure presentation swap: Manage's room-unavailable bare paragraph.
 *
 * ===================== ANCHORED, NOT CENTRED IN THE VIEWPORT ===============
 * The outer element takes the full width it is given and centres a bounded panel
 * inside it, so the panel lands on the content column's axis whatever that
 * column is. It does NOT reach for the viewport, and it does not set its own
 * `max-w-[1180px]`: a primitive that hardcoded one surface's column would be
 * wrong on the next one. Give it the column and it fills it.
 *
 * ===================== TONE IS A FACT ABOUT THE CAUSE ======================
 * `tone="neutral"` is the default and is the right answer for nothing-to-show.
 * `tone="error"` is only for a state that FAILED, and it is the only thing in
 * this file that changes colour, because the brief's rule is that an empty state
 * is never error-flavoured unless something actually broke. Prep's empty notice
 * and Prep's read-failure notice currently share one component and one look,
 * which is exactly how an ordinary state came to look like a fault.
 *
 * ===================== THE ACTION SLOT IS OPTIONAL AND STAYS EMPTY =========
 * "No button unless a meaningful next action exists" is in the brief verbatim.
 * The slot renders nothing at all when no action is passed, rather than
 * rendering a disabled control or a dead link.
 */

const TONE = {
  neutral: "border-white/[0.06] bg-background-cloudburst/40",
  error: "border-persimmon/25 bg-background-cloudburst/40",
};

export const EmptyState = ({
  title,
  tone = "neutral",
  action = null,
  testId,
  className = "",
  children,
  ...rest
}) => (
  <div className={cn("w-full", className)} data-testid={testId} {...rest}>
    <div
      className={cn(
        "mx-auto flex max-w-[52ch] flex-col items-center gap-3 rounded-lg border px-6 py-12 text-center",
        TONE[tone] ?? TONE.neutral,
      )}
    >
      {title ? <p className="text-md text-lv0">{title}</p> : null}
      {children ? (
        <div className="max-w-[46ch] text-sm text-lv2">{children}</div>
      ) : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  </div>
);

export default EmptyState;
